import { DocCategoryDto } from '../model/AdminDocument/DocCategoryDto';
import { GeneralDocFolderContentsDto } from '../model/AdminDocument/GeneralDocFolderContentsDto';
import { DocFolderDto } from '../model/AdminDocument/DocFolderDto';
import { AdminDocumentSearchResponseDto } from '../model/AdminDocument/AdminDocumentSearchDto';
import { GetDocumentTypeListQuery } from '../model/GetDocumentTypeListQuery';
import { getDocument, fetchDocCategory, fetchDocFolder, getDocumentTypeList, createDocFolder, renameDocFolder,  deleteAdminDocumentList, adminDocumentSearch, fetchAdminFolderDocumentCount, deleteAdminFolder } from '../services/DocuMateAPI';
import { DeleteAdminFolderResponseDto } from '../model/AdminDocument/DeleteAdminFolderResponseDto';
import Logger from '../../shared/services/Logger';
import * as constant from '../../shared/util/constant';
import { addNote, getNotesByEntityId } from '../services/DocuMateAPI';
import { NoteDto } from '../model/AdminDocument/NoteDto';

class AdminDocumentService {
    addNote = async (entityId: string, notes: string): Promise<NoteDto> => {
        try {
            return await addNote(entityId, notes);
        } catch (error) {
            Logger.logError(`Error adding note for ${entityId}: ${error?.toString()}`);
            throw error;
        }
    };

    getNotesByEntityId = async (entityId: string): Promise<NoteDto[]> => {
        try {
            return await getNotesByEntityId(entityId);
        } catch (error) {
            Logger.logError(`Error fetching notes for ${entityId}: ${error?.toString()}`);
            throw error;
        }
    };
   
    getDocument = async (docFileId: string, convertToPDF: boolean = true): Promise<{ blob: Blob; filename: string; contentType?: string }> => {
        try {
            const response = await getDocument(docFileId, false, convertToPDF);
            return response;
        } catch (error) {
            Logger.logError(`Error fetching document ${docFileId}: ${error?.toString()}`);
            throw error;
        }
    };
    
    fetchDocCategory = async (): Promise<DocCategoryDto[]> => {
        try {
            const response = await fetchDocCategory();
            return response;
        } catch (error) {
            Logger.logError(`Error fetching doc categories: ${error?.toString()}`);
            throw error;
        }
    };
    
    fetchDocFolder = async (appId: number, categoryId?: string, parentFolderId?: string): Promise<GeneralDocFolderContentsDto> => {
        try {
            const response = await fetchDocFolder(appId, categoryId, parentFolderId);
            return response;
        } catch (error) {
            Logger.logError(`Error fetching doc folder contents: ${error?.toString()}`);
            throw error;
        }
    };

    // Soft delete via GeneralDocStorage, the same service the tree and document list are read from.
    deleteDocument = async (appId: number, docFileIds: string[]): Promise<string[]> => {
        try {
            const response = await deleteAdminDocumentList(appId, docFileIds);
            return response;
        } catch (error) {
            Logger.logError(`Error deleting document(s) ${docFileIds?.join(', ')}: ${error?.toString()}`);
            throw error;
        }
    };

    getListOfDocumentType = async (query: GetDocumentTypeListQuery): Promise<{ value: string; label: string }[]> => {
        try {
            const response = await getDocumentTypeList(query);
            return response.map(item => ({ value: item.id, label: item.type }));
        } catch (error) {
            Logger.logError(`Error fetching document types: ${error?.toString()}`);
            throw error;
        }
    };

    createFolder = async (
        name: string,
        categoryId?: string,
        parentFolderId?: string
    ): Promise<DocFolderDto> => {
        try {
            const response = await createDocFolder(name, categoryId, parentFolderId);
            return response;
        } catch (error) {
            Logger.logError(`Error creating folder: ${error?.toString()}`);
            throw error;
        }
    };

    renameFolder = async (
        folderId: string,
        name: string
    ): Promise<DocFolderDto> => {
        try {
            const response = await renameDocFolder(folderId, name);
            return response;
        } catch (error) {
            Logger.logError(`Error renaming folder ${folderId}: ${error?.toString()}`);
            throw error;
        }
    };

    fetchAdminFolderDocumentCount = async (appId: number, folderId: string): Promise<number> => {
        try {
            const response = await fetchAdminFolderDocumentCount(appId, folderId);
            return response;
        } catch (error) {
            Logger.logError(`Error fetching document count for folder ${folderId}: ${error?.toString()}`);
            throw error;
        }
    };

    deleteFolder = async (appId: number, folderId: string): Promise<DeleteAdminFolderResponseDto> => {
        try {
            const response = await deleteAdminFolder(appId, folderId);
            return response;
        } catch (error) {
            Logger.logError(`Error deleting folder ${folderId}: ${error?.toString()}`);
            throw error;
        }
    };

    searchAdminDocuments = async (searchText: string, onlyFolderSearch: boolean = false): Promise<AdminDocumentSearchResponseDto> => {
        try {
            const response = await adminDocumentSearch(searchText, onlyFolderSearch);
            return { folder: response?.folder ?? [], document: response?.document ?? [] };
        } catch (error) {
            Logger.logError(`Error searching admin documents for "${searchText}": ${error?.toString()}`);
            throw error;
        }
    };
}

export default AdminDocumentService;
